import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT, SMART_SEARCH_PARAMS } from '~/constants';

import { smartSearchKeys } from './keys';

import type { TypeGetSmartSearchResponse, TypeSmartSearchResult } from '~/types/smart-search.types';

/**
 * [스마트검색] 스마트 검색 API
 */
const fetchSmartSearch = async (query: string): Promise<TypeSmartSearchResult> => {
  const { data } = await axiosInstance.get<TypeGetSmartSearchResponse>(API_ENDPOINT.DELIVERY_AGENCY.GET_SMART_SEARCH, {
    params: { query, productCode: SMART_SEARCH_PARAMS.PRODUCT_CODE, serviceType: SMART_SEARCH_PARAMS.SERVICE_TYPE },
  });
  return data.data;
};

/**
 * [스마트검색] 스마트 검색 Query Hook
 * - enabled: query가 있을 때만 실행
 * - meta.silent: 400 에러(code 60000)는 컴포넌트에서 직접 처리
 */
export const useGetSmartSearch = (query: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: smartSearchKeys.search(query),
    queryFn: () => fetchSmartSearch(query),
    enabled: (options?.enabled ?? true) && !!query,
    gcTime: 1000 * 60 * 5,
    meta: { silent: true, background: true },
  });
};

registerMockAPI({
  method: 'get',
  endpoint: '/api-delivery-agency/smart-search',
  label: '스마트검색/검색',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          queryId: 'mock-query-001',
          answer:
            "[1] 좌측 메뉴에서 [배송 관리] > [주문 목록]으로 이동\n[2] 환불 대상 주문건을 검색하여 클릭\n[3] 주문 상세 페이지 하단의 '환불하기' 버튼을 선택\n[4] 환불 사유를 선택하고, 환불 금액을 확인한 후 '환불 요청' 버튼을 클릭\n[5] 환불은 영업일 기준 3~5일 내에 처리",
          relatedQuestions: [
            { faqId: 1, question: '환불 처리는 어떻게 하나요?' },
            { faqId: 2, question: '배송 조회는 어디서 하나요?' },
            { faqId: 3, question: '정산 내역은 어떻게 확인하나요?' },
          ],
        },
      },
    },
    {
      name: '관련 질문 없음',
      response: {
        data: {
          queryId: 'mock-query-002',
          answer: '배송 완료 시간은 일반적으로 오전 7시 이전입니다.',
          relatedQuestions: [],
        },
      },
    },
    {
      name: '정확한 답변 불가 (60000)',
      response: { error: { code: 60000, message: '정확한 답변이 어렵습니다.' } },
      status: 400,
    },
    {
      name: '정상 응답 — 10초 지연',
      response: {
        data: {
          queryId: 'mock-query-003',
          answer: '지연 응답 테스트입니다.',
          relatedQuestions: [],
        },
      },
      delay: 10000,
    },
  ],
});
